<template>
  <Dialog
    :open="open"
    :class="sizeClass"
    @update:open="onOpenChange"
  >
    <DialogContent>
      <!-- Header -->
      <DialogHeader v-if="title" class="bg-muted/50 border-b">
        <DialogTitle>
          {{ title }}
        </DialogTitle>
      </DialogHeader>

      <!-- Body -->
      <DialogBody class="max-h-[70vh] overflow-y-auto">
        <div class="space-y-4">
          <!-- Description -->
          <p v-if="description" class="text-sm text-muted-foreground">
            {{ description }}
          </p>

          <!-- JDynamicForm (schema 없으면 렌더하지 않음 — body 슬롯 전용 모드) -->
          <JDynamicForm
            v-if="schema"
            ref="dynamicFormRef"
            :schema="schema"
            :model-value="internalFormValue"
            @update:model-value="handleFormChange"
            @submit="handleFormSubmit"
            @change="handleFieldChange"
            @error="handleFormError"
          />

          <!-- 커스텀 컨텐츠 슬롯 -->
          <slot name="body" />
        </div>
      </DialogBody>

      <!-- Footer -->
      <DialogFooter>
        <!-- 확인/취소 버튼 -->
        <template v-if="buttonType === 'OkCancel'">
          <JButton
            variant="outline"
            @click="handleCancel"
          >
            {{ cancelText }}
          </JButton>
          <JButton
            :variant="confirmVariant"
            :disabled="confirmDisabled"
            @click="handleConfirm"
          >
            {{ confirmText }}
          </JButton>
        </template>
        
        <!-- 확인 버튼만 -->
        <template v-else-if="buttonType === 'Ok'">
          <JButton
            :variant="confirmVariant"
            :disabled="confirmDisabled"
            class="w-full"
            @click="handleConfirm"
          >
            {{ confirmText }}
          </JButton>
        </template>

        <!-- 커스텀 푸터 슬롯 -->
        <slot name="footer" />
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/shadcn'
import { JButton } from '@/components/atoms'
import { JDynamicForm } from '@/components/organisms'
import type { FormSchema } from '@/types/dynamic-form'

export interface JFormModalProps {
  // 모달 표시 여부
  open: boolean
  // 헤더
  title?: string
  description?: string
  // 다이나믹 폼 스키마 (선택 — 없으면 body 슬롯 전용 모달로 동작)
  schema?: FormSchema
  // 폼 값
  modelValue?: Record<string, any>
  // 모달 사이즈 (유동적)
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full'
  // 버튼 설정
  buttonType?: 'Ok' | 'OkCancel'
  confirmText?: string
  cancelText?: string
  confirmVariant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link'
  confirmDisabled?: boolean
}

const props = withDefaults(defineProps<JFormModalProps>(), {
  open: false,
  size: 'lg',
  buttonType: 'OkCancel',
  confirmText: '확인',
  cancelText: '취소',
  confirmVariant: 'default',
  confirmDisabled: false,
})

const emit = defineEmits<{
  'update:open': [value: boolean]
  'update:modelValue': [value: Record<string, any>]
  'confirm': [value: Record<string, any>]
  'cancel': []
  'submit': [value: Record<string, any>]
  'change': [data: { field: string; value: any }]
  'error': [errors: any]
}>()

// 다이나믹 폼 ref
const dynamicFormRef = ref<InstanceType<typeof JDynamicForm> | null>(null)

// 내부 폼 값 관리
const internalFormValue = ref<Record<string, any>>(props.modelValue || {})
// 확인 버튼 클릭 이후 검증 성공 시점에만 confirm 이벤트를 방출하기 위한 플래그
const isConfirming = ref<boolean>(false)

// 사이즈 클래스 계산
const sizeClass = computed(() => {
  // Dialog.vue에 기본 max-w-lg가 하드코딩되어 있어 !max-w-*로 오버라이드
  const sizeMap: Record<NonNullable<JFormModalProps['size']>, string> = {
    'sm': '!max-w-sm',
    'md': '!max-w-md',
    'lg': '!max-w-2xl',
    'xl': '!max-w-4xl',
    '2xl': '!max-w-6xl',
    'full': '!max-w-[95vw]'
  }
  return sizeMap[props.size]
})

// props.modelValue 변경 감지
watch(() => props.modelValue, (newValue) => {
  if (newValue != null) {
    internalFormValue.value = { ...newValue }
  } else {
    internalFormValue.value = {}
  }
}, { deep: true })

// 폼 변경 핸들러
const handleFormChange = (value: Record<string, any>) => {
  internalFormValue.value = value
  emit('update:modelValue', value)
}

// 필드 변경 핸들러
const handleFieldChange = (data: { field: string; value: any }) => {
  emit('change', data)
}

// 폼 제출 핸들러
const handleFormSubmit = (value: Record<string, any>) => {
  emit('submit', value)
  if (isConfirming.value) {
    emit('confirm', value)
    isConfirming.value = false
  }
}

// 폼 에러 핸들러
const handleFormError = (errors: any) => {
  emit('error', errors)
  // 검증 실패 시 confirm 방출을 차단
  isConfirming.value = false
}

// 모달 열기/닫기 핸들러
const onOpenChange = (value: boolean) => {
  emit('update:open', value)
}

// 확인 버튼 핸들러
const handleConfirm = () => {
  // schema 없는 body 슬롯 전용 모드: 폼 검증을 건너뛰고 바로 confirm 방출
  if (!props.schema) {
    emit('confirm', internalFormValue.value)
    return
  }
  // 폼 제출 트리거 (검증 성공 시점에 handleFormSubmit에서 confirm 방출)
  isConfirming.value = true
  if (dynamicFormRef.value) {
    dynamicFormRef.value.submit()
  }
}

// 취소 버튼 핸들러
const handleCancel = () => {
  emit('cancel')
  // 폼 리셋
  if (dynamicFormRef.value) {
    dynamicFormRef.value.reset()
  }
}

// 외부에서 접근 가능한 메서드
defineExpose({
  reset: () => dynamicFormRef.value?.reset(),
  submit: () => dynamicFormRef.value?.submit(),
  formState: computed(() => dynamicFormRef.value?.formState),
})
</script>
